/* eslint-disable @typescript-eslint/no-magic-numbers */
import omit from 'lodash/omit';
import { darken, shade, tint, transparentize } from 'polished';
import {
  adjustColorForProperContrast,
  generateColorPalette,
  getBestColorForText,
  isDarkColor,
  isVeryDarkColor,
} from '~/components/AccessibleColorPicker/color-palette-logic';
import { colorHexWithHash } from '~/singlePageApp/components/DropdownColorPicker/colorUtils';
import { stringsAreEqual } from '~/utilities/strings';
import type { EventBrandValuesSet, NonGeneratedEventBrandValues } from '../Brand/types';
import { WISTIA_BLUE, WHITE_WITH_HEX, DARK_COLOR_WITH_HEX } from './utilities';
import {
  getColorsToExcludeFromText,
  getBrandDarkBackgroundColor,
  getBrandLightBackgroundColor,
  getHeavyAccentedBackgroundColor,
  getSlightAccentedBackgroundColor,
} from './colorGen';

export const getGeneratedValuesForBrand = (
  brand: NonGeneratedEventBrandValues,
  isForEventRoom = false,
  // eslint-disable-next-line sonarjs/cognitive-complexity
): EventBrandValuesSet => {
  const isUsingDefaultColors =
    brand.primaryColor === colorHexWithHash(WISTIA_BLUE) &&
    (brand.backgroundColor === WHITE_WITH_HEX || brand.backgroundColor === DARK_COLOR_WITH_HEX);

  const brandDarkBackgroundColor = isUsingDefaultColors
    ? DARK_COLOR_WITH_HEX
    : getBrandDarkBackgroundColor(brand.primaryColor);

  const brandLightBackgroundColor = isUsingDefaultColors
    ? WHITE_WITH_HEX
    : getBrandLightBackgroundColor(brand.primaryColor);

  const defaultBackgroundColor = isUsingDefaultColors ? WHITE_WITH_HEX : brandDarkBackgroundColor;

  // For a new Live Stream Event, if users do not have an account-level player color,
  // we will assign a default background color of white in the LSE model.
  // Otherwise background color is nil by default.
  let backgroundColor = brand.backgroundColor ?? defaultBackgroundColor;

  if (
    stringsAreEqual(backgroundColor, brandDarkBackgroundColor, true) ||
    stringsAreEqual(backgroundColor, brandLightBackgroundColor, true)
  ) {
    backgroundColor = brand.isDarkMode ? brandDarkBackgroundColor : brandLightBackgroundColor;
    // eslint-disable-next-line sonarjs/elseif-without-else
  } else if (
    stringsAreEqual(backgroundColor, DARK_COLOR_WITH_HEX, true) ||
    stringsAreEqual(backgroundColor, WHITE_WITH_HEX, true)
  ) {
    backgroundColor = brand.isDarkMode ? DARK_COLOR_WITH_HEX : WHITE_WITH_HEX;
  }

  let backgroundGradientColor = isDarkColor(backgroundColor)
    ? tint(0.2, backgroundColor)
    : darken(0.2, backgroundColor);
  if (backgroundColor === '#ffffff') {
    backgroundGradientColor = '#ffffff';
  }

  const slightAccentedBackgroundColor = getSlightAccentedBackgroundColor(
    backgroundColor,
    isForEventRoom,
  );

  const heavyAccentedBackgroundColor = getHeavyAccentedBackgroundColor(backgroundColor);

  const hasDarkBackgroundColor = isDarkColor(backgroundColor);

  const brandColorPalette = generateColorPalette([
    backgroundColor,
    brand.primaryColor,
    slightAccentedBackgroundColor,
    heavyAccentedBackgroundColor,
  ]);

  // We don't want to consider the primary color or the variations of background colors when we get the main text color,
  // but if any of those values match our default light/dark colors, we should keep them as options for the text color.
  const colorsToExcludeFromMainTextColor = getColorsToExcludeFromText([
    brand.primaryColor,
    slightAccentedBackgroundColor,
    heavyAccentedBackgroundColor,
    brandDarkBackgroundColor,
    brandLightBackgroundColor,
  ]);

  const colorsToExcludeFromHeadingTextColor = getColorsToExcludeFromText([brand.primaryColor]);

  const mainTextColor = getBestColorForText(
    brandColorPalette,
    colorsToExcludeFromMainTextColor,
    backgroundColor,
  );

  const headingTextColor = getBestColorForText(
    brandColorPalette,
    colorsToExcludeFromHeadingTextColor,
    backgroundColor,
    'largeText',
  );

  const colorsToExcludeFromPrimaryTextColor = getColorsToExcludeFromText([
    backgroundColor,
    brand.primaryColor,
    slightAccentedBackgroundColor,
    heavyAccentedBackgroundColor,
    brandDarkBackgroundColor,
    brandLightBackgroundColor,
  ]);

  const primaryTextColor = getBestColorForText(
    brandColorPalette,
    colorsToExcludeFromPrimaryTextColor,
    brand.primaryColor,
  );

  const slightAccentedTextColor = getBestColorForText(
    brandColorPalette,
    colorsToExcludeFromPrimaryTextColor,
    slightAccentedBackgroundColor,
  );

  const slightAccentedShadowColor = isVeryDarkColor(slightAccentedBackgroundColor)
    ? transparentize(0.3, tint(0.5, slightAccentedBackgroundColor))
    : transparentize(0.3, shade(0.5, slightAccentedBackgroundColor));

  const heavyAccentedTextColor = getBestColorForText(
    brandColorPalette,
    getColorsToExcludeFromText([backgroundColor, brand.primaryColor]),
    heavyAccentedBackgroundColor,
  );

  const secondaryButtonHoverBackgroundColor = isDarkColor(backgroundColor)
    ? tint(0.2, backgroundColor)
    : darken(0.1, backgroundColor);

  const secondaryButtonHoverTextColor = getBestColorForText(
    brandColorPalette,
    colorsToExcludeFromPrimaryTextColor,
    secondaryButtonHoverBackgroundColor,
  );

  const primaryButtonHoverBackgroundColor = isDarkColor(brand.primaryColor)
    ? tint(0.2, brand.primaryColor)
    : shade(0.1, brand.primaryColor);

  const primaryButtonHoverTextColor = getBestColorForText(
    brandColorPalette,
    colorsToExcludeFromPrimaryTextColor,
    primaryButtonHoverBackgroundColor,
  );

  const borderColor = hasDarkBackgroundColor
    ? tint(0.3, backgroundColor)
    : darken(0.5, backgroundColor);

  const slightAccentedBorderColor = isDarkColor(slightAccentedBackgroundColor)
    ? tint(0.3, slightAccentedBackgroundColor)
    : darken(0.3, slightAccentedBackgroundColor);

  const heavyAccentedBorderColor = isDarkColor(heavyAccentedBackgroundColor)
    ? tint(0.3, heavyAccentedBackgroundColor)
    : darken(0.3, heavyAccentedBackgroundColor);

  const speakerTileTitleTextColor = isDarkColor(slightAccentedBackgroundColor)
    ? darken(0.1, slightAccentedTextColor)
    : tint(0.3, slightAccentedTextColor);

  const hasWhiteBg = backgroundColor === WHITE_WITH_HEX;
  const speakerTileBorder = hasWhiteBg ? '1px solid #E0E0E0' : 'none';
  const speakerTileBackgroundColor = hasWhiteBg ? WHITE_WITH_HEX : slightAccentedBackgroundColor;

  const playerColor = adjustColorForProperContrast(brand.primaryColor, '#ffffff', 'nonText');

  return omit(
    {
      ...brand,
      backgroundColor,
      backgroundGradientColor,
      brandDarkBackgroundColor,
      brandLightBackgroundColor,
      slightAccentedBackgroundColor,
      slightAccentedTextColor,
      slightAccentedShadowColor,
      slightAccentedBorderColor,
      heavyAccentedBackgroundColor,
      heavyAccentedTextColor,
      heavyAccentedBorderColor,
      headingTextColor,
      mainTextColor,
      playerColor,
      primaryTextColor,
      borderColor,
      speakerTileBackgroundColor,
      speakerTileBorder,
      speakerTileTextColor: slightAccentedTextColor,
      speakerTileTitleTextColor,
      speakerTileShadowColor: slightAccentedShadowColor,
      speakerSocialStrokeColor: slightAccentedShadowColor,
      speakerSocialFillColor: slightAccentedTextColor,
      primaryButtonBackgroundColor: brand.primaryColor,
      primaryButtonTextColor: primaryTextColor,
      primaryButtonBorderColor: brand.primaryColor,
      primaryButtonHoverBackgroundColor,
      primaryButtonHoverTextColor,
      secondaryButtonTextColor: mainTextColor,
      secondaryButtonBorderColor: mainTextColor,
      secondaryButtonHoverBackgroundColor,
      secondaryButtonHoverTextColor,
      subtitleFontSize: brand.titleFontSize * 0.6,
    },
    '__typename',
  );
};
